import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
}

// Unified page header. Replaces the recurring Tag + h2 + <br /> + span pattern.
// Title relies on text-wrap: balance (set globally on h1-h4) so it never
// breaks awkwardly mid-phrase on narrow viewports.
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  align = 'left',
  className,
}: PageHeaderProps) {
  const isCenter = align === 'center';
  return (
    <header
      className={cn(
        'flex flex-col gap-4',
        isCenter ? 'items-center text-center' : 'items-start',
        className,
      )}
    >
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h1
        className={cn(
          'font-display text-[2rem] font-semibold leading-[1.1] tracking-tight md:text-[2.5rem]',
          isCenter ? 'max-w-[18ch]' : 'max-w-[20ch]',
        )}
      >
        {title}
      </h1>
      {description ? (
        <p
          className={cn(
            'text-base leading-relaxed text-on-surface-variant',
            isCenter ? 'max-w-prose' : 'max-w-2xl',
          )}
        >
          {description}
        </p>
      ) : null}
      {actions ? (
        <div className={cn('flex flex-wrap gap-3 pt-1', isCenter && 'justify-center')}>
          {actions}
        </div>
      ) : null}
    </header>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-on-surface-variant">
      <span
        className="h-1.5 w-1.5 rounded-full bg-cyan"
        style={{ boxShadow: '0 0 10px var(--accent-glow)' }}
      />
      {children}
    </span>
  );
}

// Standalone export to use inside breadcrumb-only headers.
export { Eyebrow as PageEyebrow };
